import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Checkbox, ConfigProvider, Form, Input } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { login, isLoggedIn } from '../services/authServices';
import { useLocale } from '../i18n/LocaleProvider';

const BRAND = '#1D2C7B';

function Login() {
    const { t } = useTranslation();
    const { lang, setLang, langs } = useLocale();
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();
    const siteTitle = (typeof window !== 'undefined' && window.env?.SITE_TITLE) || 'Dr. OXU';

    useEffect(() => {
        if (isLoggedIn()) {
            navigate('/', { replace: true });
        }
    }, [navigate]);

    const handleLogin = async (values) => {
        setSubmitting(true);
        try {
            const response = await login(String(values.login || '').trim(), values.password);
            if (response.success) {
                toast.success(t('login.success'));
                navigate('/', { replace: true });
            } else {
                toast.error(t('login.error'));
            }
        } catch {
            toast.error(t('login.error'));
        } finally {
            setSubmitting(false);
        }
    };

    if (isLoggedIn()) {
        return null;
    }

    const languageSwitch = (
        <div className="flex gap-1 rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            {langs.map((item) => (
                <button
                    key={item.code}
                    type="button"
                    onClick={() => setLang(item.code)}
                    className={`min-w-[2.25rem] rounded-md px-2 py-1 text-[11px] font-semibold transition-colors ${
                        lang === item.code
                            ? 'bg-[#1D2C7B] text-white'
                            : 'text-slate-600 hover:bg-white'
                    }`}
                >
                    {item.short}
                </button>
            ))}
        </div>
    );

    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: BRAND,
                    borderRadius: 8,
                    controlHeight: 44,
                },
            }}
        >
            <div className="login-page flex min-h-screen bg-[#f4f6f9]">
                <aside className="login-page__panel relative hidden w-[44%] flex-col justify-between overflow-hidden px-12 py-12 text-white lg:flex">
                    <div>
                        <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/60">
                            {t('common.dentalClinic')}
                        </p>
                        <h1 className="mt-3 mb-0 text-3xl font-semibold leading-tight tracking-tight">
                            {siteTitle}
                        </h1>
                        <p className="mt-4 mb-0 max-w-sm text-base leading-relaxed text-white/75">
                            {t('login.panelText')}
                        </p>
                    </div>

                    <ul className="m-0 list-none space-y-3 p-0 text-sm text-white/80">
                        <li className="flex items-center gap-3">
                            <span className="h-1.5 w-1.5 rounded-full bg-white/80" />
                            {t('nav.home')}
                        </li>
                        <li className="flex items-center gap-3">
                            <span className="h-1.5 w-1.5 rounded-full bg-white/80" />
                            {t('nav.patients')}
                        </li>
                        <li className="flex items-center gap-3">
                            <span className="h-1.5 w-1.5 rounded-full bg-white/80" />
                            {t('nav.appointments')}
                        </li>
                    </ul>
                </aside>

                <main className="flex flex-1 items-center justify-center px-4 py-8 sm:px-8">
                    <div className="login-page__card w-full max-w-[400px]">
                        <div className="mb-6 flex items-center justify-between gap-3 lg:justify-end">
                            <div className="flex min-w-0 items-center gap-3 lg:hidden">
                                <img
                                    src="/logo/logo.png"
                                    alt={t('login.logoAlt')}
                                    className="h-11 w-auto object-contain"
                                />
                                <div className="min-w-0">
                                    <p className="m-0 truncate text-sm font-semibold text-slate-800">{siteTitle}</p>
                                    <p className="m-0 truncate text-[11px] text-slate-500">{t('common.dentalClinic')}</p>
                                </div>
                            </div>
                            {languageSwitch}
                        </div>

                        <section className="rounded-xl border border-slate-200/90 bg-white px-5 py-6 sm:px-7 sm:py-8">
                            <div className="mb-6 hidden justify-center lg:flex">
                                <img
                                    src="/logo/logo.png"
                                    alt={t('login.logoAlt')}
                                    className="h-16 w-auto object-contain"
                                />
                            </div>

                            <h2 className="m-0 text-xl font-semibold text-slate-800">{t('login.welcome')}</h2>
                            <p className="mt-1 mb-6 text-sm text-slate-500">{t('login.subtitle')}</p>

                            <Form
                                layout="vertical"
                                requiredMark={false}
                                onFinish={handleLogin}
                                initialValues={{ remember: true }}
                            >
                                <Form.Item
                                    name="login"
                                    label={<span className="text-sm font-medium text-slate-700">{t('common.login')}</span>}
                                    rules={[{ required: true, message: t('common.required') }]}
                                >
                                    <Input
                                        size="large"
                                        prefix={<UserOutlined className="text-slate-400" />}
                                        placeholder={t('common.login')}
                                        autoComplete="username"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="password"
                                    label={<span className="text-sm font-medium text-slate-700">{t('common.password')}</span>}
                                    rules={[{ required: true, message: t('common.required') }]}
                                >
                                    <Input.Password
                                        size="large"
                                        prefix={<LockOutlined className="text-slate-400" />}
                                        placeholder={t('common.password')}
                                        autoComplete="current-password"
                                    />
                                </Form.Item>

                                <Form.Item name="remember" valuePropName="checked" className="!mb-5">
                                    <Checkbox>{t('common.rememberMe')}</Checkbox>
                                </Form.Item>

                                <Button
                                    htmlType="submit"
                                    type="primary"
                                    loading={submitting}
                                    className="!h-11 w-full !rounded-lg !text-sm !font-semibold"
                                    style={{ backgroundColor: BRAND, borderColor: BRAND }}
                                >
                                    {submitting ? t('login.signingIn') : t('common.signIn')}
                                </Button>
                            </Form>
                        </section>
                    </div>
                </main>
            </div>
        </ConfigProvider>
    );
}

export default Login;
